import api from '../api';
import type {
  RoastedBulkGroup,
  RoastedBulkGroupItem,
  RoastedBulkAdjustmentReason,
  RoastedBulkGroupAdjustmentResult,
} from '../../types/inventory';
import { mapDtoToRoastedBulkGroup, mapDtoToRoastedBulkGroupItem } from './inventoryMappers';
import { cache } from '../../utils/cache';

export interface GreenBeanPickerItem {
  id: string;
  code: string;
  name: string;
}

export interface RoastedBulkImportPayloadRow {
  source_green_bean_item_id: string;
  initial_qty: number;
  standard_cost: number;
  roast_type: 'Espresso' | 'Filter' | 'Omni' | 'Sample';
}

export interface RoastedBulkImportResponse {
  created: number;
  item_ids: string[];
  errors?: { row_index: number; field: string; message: string }[];
}

export const roastedBulkService = {
  getRoastedBulkGroups: async (
    includeItems = false,
    options: {
      excludeOrphans?: boolean;
      status?: 'active' | 'inactive' | 'all';
      includeOnboardingSamples?: boolean;
    } = {},
  ): Promise<RoastedBulkGroup[]> => {
    try {
      const response = await api.get('/inventory/roasted-bulks-groups', {
        params: {
          include_items: includeItems,
          ...(options.excludeOrphans ? { exclude_orphans: true } : {}),
          ...(options.status ? { status: options.status } : {}),
          ...(options.includeOnboardingSamples ? { include_onboarding_samples: true } : {}),
        },
      });
      const groups = response.data?.groups ?? response.data ?? [];
      if (!Array.isArray(groups)) {
        return [];
      }
      return groups.map(mapDtoToRoastedBulkGroup);
    } catch (error) {
      console.error('Error fetching roasted bulk groups:', error);
      return [];
    }
  },

  getRoastedBulkGroupItems: async (itemId: string, roastType: string): Promise<RoastedBulkGroupItem[]> => {
    try {
      const response = await api.get('/inventory/roasted-bulks-groups/items', {
        params: { item_id: itemId, roast_type: roastType },
      });
      const items = response.data?.items ?? response.data ?? [];
      if (!Array.isArray(items)) {
        return [];
      }
      return items.map(mapDtoToRoastedBulkGroupItem);
    } catch (error) {
      console.error('Error fetching roasted bulk group items:', error);
      return [];
    }
  },

  // Unlike the two getters above, this deliberately does NOT swallow errors:
  // the modal must surface a rejected write-off (insufficient stock, bad reason)
  // instead of silently reporting success.
  adjustRoastedBulkGroupStock: async (payload: {
    groupId: string;
    roastType: string;
    quantityKg: number;
    reason: RoastedBulkAdjustmentReason;
    notes: string;
  }): Promise<RoastedBulkGroupAdjustmentResult> => {
    const response = await api.post('/inventory/roasted-bulks-groups/adjust', {
      group_id: payload.groupId,
      roast_type: payload.roastType,
      quantity_kg: payload.quantityKg,
      reason: payload.reason,
      notes: payload.notes,
    });
    cache.invalidate('inventory');
    cache.invalidate('reports_');
    return {
      adjustmentId: response.data.adjustment_id,
      totalQuantityKg: response.data.total_quantity_kg,
      totalCost: response.data.total_cost,
      allocations: response.data.allocations ?? [],
    };
  },

  listGreenBeansForRBImportPicker: async (): Promise<GreenBeanPickerItem[]> => {
    const response = await api.get<GreenBeanPickerItem[]>(
      '/inventory/import/roasted-bulks/green-beans',
    );
    return response.data ?? [];
  },

  downloadRoastedBulkImportTemplate: async (greenBeanItemIds: string[]): Promise<void> => {
    const response = await api.post(
      '/inventory/import/roasted-bulks/template',
      { green_bean_item_ids: greenBeanItemIds },
      { responseType: 'blob' },
    );
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'roasted-bulk-import-template.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  importRoastedBulks: async (
    rows: RoastedBulkImportPayloadRow[],
  ): Promise<RoastedBulkImportResponse> => {
    const response = await api.post('/inventory/import/roasted-bulks', { rows });
    cache.invalidate('inventory');
    cache.invalidate('reports_');
    return response.data;
  },
};
